/*
 * Buying Journey: review visualisation, renderer.
 *
 * Draws the spec produced by journey-viz-spec.js with Three.js. The library is
 * vendored as a theme asset and loaded lazily via dynamic import() only when
 * the customer actually reaches the review step, so the ~650KB never touches
 * the earlier steps.
 *
 * Skywalks products render as stylised parametric stand-ins for now. Real
 * per-product GLB models can replace them later without touching the spec
 * layer; the kind -> mesh mapping in buildItem() is the single seam.
 *
 * Progressive enhancement only. If WebGL is unavailable or the import fails,
 * the container hides and the textual review carries on untouched.
 */
(function () {
  'use strict';

  function Viz(root) {
    this.root = root;
    this.config = this.readConfig();
    if (!this.config) return;

    this.container = root.querySelector('[data-viz-container]');
    this.captionEl = root.querySelector('[data-viz-caption]');
    if (!this.container) return;

    this.THREE = null;
    this.renderer = null;
    this.scene = null;
    this.camera = null;
    this.contentGroup = null;
    this.frame = null;

    // Orbit state.
    this.azimuth = -0.7;
    this.polar = 1.05;
    this.distance = 10;
    this.target = null;
    this.dragging = false;
    this.lastPointer = null;

    this.journey = null;
    this.bind();
  }

  Viz.prototype.readConfig = function () {
    var el = this.root.querySelector('[data-viz-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      return null;
    }
  };

  Viz.prototype.bind = function () {
    var self = this;

    function attach(journey) {
      self.journey = journey;
      if (journey.state.current === self.config.stepId) self.ensure();
    }

    if (window.CatnetsJourney) {
      attach(window.CatnetsJourney);
    } else {
      document.addEventListener('journey:ready', function (e) {
        attach(e.detail.journey);
      });
    }

    document.addEventListener('journey:step:enter', function (e) {
      if (e.detail.step === self.config.stepId) self.ensure();
    });

    document.addEventListener('journey:step:complete', function () {
      if (self.THREE && self.journey && self.journey.state.current === self.config.stepId) {
        try {
          self.rebuild();
        } catch (err) {
          self.fail('rebuild threw', err);
        }
      }
    });
  };

  /**
   * Every hide path says why in the console. A silent hide earlier made this
   * feature undebuggable from a screenshot; never again.
   */
  Viz.prototype.fail = function (reason, err) {
    this.container.hidden = true;
    if (window.console && console.warn) {
      console.warn('[Catnets viz] hidden: ' + reason, err || '');
    }
  };

  /**
   * Lazy-load Three.js once, then build.
   *
   * Loaded by injecting a CLASSIC script tag, deliberately not dynamic
   * import(). Shopify auto-minifies theme .js assets and its minifier
   * rewrites import() into CommonJS require(), which does not exist in
   * browsers; that shipped as ReferenceError: require is not defined and a
   * hidden preview. A classic script exposing a global has no module syntax
   * for any minifier to mangle. The asset is an IIFE build of three
   * (esbuild --bundle --format=iife --global-name=CATNETS_THREE --minify).
   */
  Viz.prototype.ensure = function () {
    var self = this;

    if (this.THREE) {
      this.rebuild();
      return;
    }

    if (this.loading) return;
    this.loading = true;

    // Bail early where WebGL cannot work.
    var probe = document.createElement('canvas');
    var gl = probe.getContext && (probe.getContext('webgl') || probe.getContext('experimental-webgl'));
    if (!gl) {
      this.fail('WebGL unavailable on this device');
      return;
    }

    function ready() {
      self.THREE = window.CATNETS_THREE;

      // Init and rebuild get their own guard so a drawing bug reports as
      // itself, not as a failed library load.
      try {
        self.init();
        self.rebuild();
      } catch (err) {
        self.fail('scene setup threw', err);
      }
    }

    if (window.CATNETS_THREE) {
      ready();
      return;
    }

    var script = document.createElement('script');
    script.src = this.config.threeUrl;
    script.async = true;
    script.onload = function () {
      if (window.CATNETS_THREE) {
        ready();
      } else {
        self.fail('Three.js loaded but CATNETS_THREE global is missing, from ' + self.config.threeUrl);
      }
    };
    script.onerror = function () {
      self.fail('Three.js failed to load from ' + self.config.threeUrl);
    };
    document.head.appendChild(script);
  };

  Viz.prototype.init = function () {
    var THREE = this.THREE;

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.container.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(45, 1, 0.1, 300);
    this.target = new THREE.Vector3(0, 1, 0);

    this.scene.add(new THREE.AmbientLight(0xffffff, 0.85));
    var sun = new THREE.DirectionalLight(0xffffff, 1.4);
    sun.position.set(6, 10, 4);
    this.scene.add(sun);

    this.bindOrbit();

    var self = this;
    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(function () {
        self.resize();
      }).observe(this.container);
    }
    this.resize();
  };

  Viz.prototype.resize = function () {
    if (!this.renderer) return;

    var w = this.container.clientWidth || 300;
    var h = this.container.clientHeight || 300;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.draw();
  };

  /** Minimal orbit: drag to rotate, wheel or pinch to zoom. */
  Viz.prototype.bindOrbit = function () {
    var self = this;
    var el = this.renderer.domElement;
    el.style.touchAction = 'none';

    el.addEventListener('pointerdown', function (e) {
      self.dragging = true;
      self.lastPointer = { x: e.clientX, y: e.clientY };
      el.setPointerCapture(e.pointerId);
    });

    el.addEventListener('pointermove', function (e) {
      if (!self.dragging) return;

      var dx = e.clientX - self.lastPointer.x;
      var dy = e.clientY - self.lastPointer.y;
      self.lastPointer = { x: e.clientX, y: e.clientY };

      self.azimuth -= dx * 0.006;
      self.polar = Math.min(1.45, Math.max(0.25, self.polar - dy * 0.005));
      self.draw();
    });

    el.addEventListener('pointerup', function () {
      self.dragging = false;
    });

    el.addEventListener('wheel', function (e) {
      e.preventDefault();
      self.distance = Math.min(60, Math.max(3, self.distance * (e.deltaY > 0 ? 1.08 : 0.92)));
      self.draw();
    }, { passive: false });
  };

  Viz.prototype.draw = function () {
    if (!this.renderer || !this.scene) return;

    var x = this.target.x + this.distance * Math.sin(this.polar) * Math.sin(this.azimuth);
    var y = this.target.y + this.distance * Math.cos(this.polar);
    var z = this.target.z + this.distance * Math.sin(this.polar) * Math.cos(this.azimuth);

    this.camera.position.set(x, y, z);
    this.camera.lookAt(this.target);
    this.renderer.render(this.scene, this.camera);
  };

  /* ------------------------------------------------------------ builders */

  /** Repeating net texture, tinted per colour, on a transparent canvas. */
  Viz.prototype.netTexture = function (colour) {
    var THREE = this.THREE;
    var c = document.createElement('canvas');
    c.width = 64;
    c.height = 64;
    var ctx = c.getContext('2d');

    ctx.clearRect(0, 0, 64, 64);
    ctx.strokeStyle = colour;
    ctx.lineWidth = 4;
    ctx.strokeRect(2, 2, 60, 60);

    var tex = new THREE.CanvasTexture(c);
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    return tex;
  };

  Viz.prototype.netPanel = function (panel) {
    var THREE = this.THREE;
    var tex = this.netTexture(panel.colour);
    // Roughly two squares per metre reads as netting at this scale.
    tex.repeat.set(Math.max(1, panel.w * 2), Math.max(1, panel.h * 2));

    var mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(panel.w, panel.h),
      new THREE.MeshLambertMaterial({
        map: tex,
        transparent: true,
        side: THREE.DoubleSide,
        depthWrite: false
      })
    );

    mesh.position.set(panel.x, panel.y, panel.z);
    mesh.rotation.x = (panel.rotX || 0) * Math.PI / 180;
    mesh.rotation.y = (panel.rotY || 0) * Math.PI / 180;
    return mesh;
  };

  Viz.prototype.box = function (w, h, d, colour) {
    var THREE = this.THREE;
    return new THREE.Mesh(
      new THREE.BoxGeometry(w, h, d),
      new THREE.MeshLambertMaterial({ color: colour })
    );
  };

  /* ---------------------------------------------- surface textures */

  /** Deterministic jitter so bricks and boards vary but never flicker. */
  function jitter(seed) {
    var x = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
    return x - Math.floor(x);
  }

  function shade(hex, amount) {
    var n = parseInt(String(hex).replace('#', ''), 16);
    var r = Math.min(255, Math.max(0, ((n >> 16) & 255) + amount));
    var g = Math.min(255, Math.max(0, ((n >> 8) & 255) + amount));
    var b = Math.min(255, Math.max(0, (n & 255) + amount));
    return 'rgb(' + r + ',' + g + ',' + b + ')';
  }

  /**
   * One square metre of surface as a tileable canvas. Drawn, not photographed,
   * so there are no image assets to manage and it ships inside this file.
   */
  Viz.prototype.surfaceCanvas = function (material, baseColour) {
    var S = 256; // canvas pixels per metre
    var c = document.createElement('canvas');
    c.width = S;
    c.height = S;
    var ctx = c.getContext('2d');

    if (material === 'brick') {
      // Standard 230x76mm bricks: ~4.3 per metre across, 13 courses high.
      ctx.fillStyle = '#b3aca3'; // mortar
      ctx.fillRect(0, 0, S, S);

      var rows = 13;
      var cols = 4;
      var bh = S / rows;
      var bw = S / cols;

      for (var row = 0; row < rows; row++) {
        var offset = (row % 2) * (bw / 2);
        for (var col = -1; col < cols + 1; col++) {
          var tone = Math.floor(jitter(row * 17 + col) * 26) - 13;
          ctx.fillStyle = shade(baseColour, tone);
          ctx.fillRect(col * bw + offset + 1.5, row * bh + 1.5, bw - 3, bh - 3);
        }
      }
    } else if (material === 'wood') {
      // Vertical fence boards, ~140mm wide, with grain streaks.
      ctx.fillStyle = baseColour;
      ctx.fillRect(0, 0, S, S);

      var boards = 7;
      var boardW = S / boards;

      for (var b = 0; b < boards; b++) {
        ctx.fillStyle = shade(baseColour, Math.floor(jitter(b * 7.3) * 22) - 11);
        ctx.fillRect(b * boardW, 0, boardW - 2, S);

        // Groove between boards.
        ctx.fillStyle = 'rgba(0,0,0,0.28)';
        ctx.fillRect(b * boardW + boardW - 2, 0, 2, S);

        // A few grain streaks per board.
        ctx.strokeStyle = 'rgba(0,0,0,0.10)';
        ctx.lineWidth = 1;
        for (var gLine = 0; gLine < 3; gLine++) {
          var gx = b * boardW + 6 + jitter(b * 31 + gLine) * (boardW - 12);
          ctx.beginPath();
          ctx.moveTo(gx, 0);
          ctx.bezierCurveTo(gx + 4, S * 0.3, gx - 4, S * 0.66, gx + 2, S);
          ctx.stroke();
        }
      }
    } else if (material === 'colorbond') {
      // Corrugated sheet: ~76mm rib pitch, 13 ribs per metre, faked curvature
      // with a light-dark-light gradient per rib.
      var ribs = 13;
      var ribW = S / ribs;

      for (var rib = 0; rib < ribs; rib++) {
        var gradient = ctx.createLinearGradient(rib * ribW, 0, (rib + 1) * ribW, 0);
        gradient.addColorStop(0, shade(baseColour, -26));
        gradient.addColorStop(0.35, shade(baseColour, 22));
        gradient.addColorStop(0.65, shade(baseColour, 8));
        gradient.addColorStop(1, shade(baseColour, -30));
        ctx.fillStyle = gradient;
        ctx.fillRect(rib * ribW, 0, ribW + 1, S);
      }
    } else {
      // Unknown material: flat colour with the faintest speckle.
      ctx.fillStyle = baseColour;
      ctx.fillRect(0, 0, S, S);
      ctx.fillStyle = 'rgba(0,0,0,0.05)';
      for (var speck = 0; speck < 140; speck++) {
        ctx.fillRect(jitter(speck) * S, jitter(speck + 999) * S, 2, 2);
      }
    }

    return c;
  };

  /**
   * Wall material with its texture repeated at true scale: one canvas tile is
   * one metre, so a 10m wall shows ten metres of brickwork rather than one
   * brick stretched to ten metres. Canvases are cached per material within a
   * rebuild; textures are per-wall because repeat counts differ.
   */
  Viz.prototype.wallMaterial = function (wall, canvasCache) {
    var THREE = this.THREE;
    var key = (wall.material || 'other') + '|' + wall.colour;

    if (!canvasCache[key]) {
      canvasCache[key] = this.surfaceCanvas(wall.material, wall.colour);
    }

    var tex = new THREE.CanvasTexture(canvasCache[key]);
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(Math.max(wall.w, 0.5), Math.max(wall.h, 0.5));
    if (THREE.SRGBColorSpace) tex.colorSpace = THREE.SRGBColorSpace;
    if (this.renderer) tex.anisotropy = this.renderer.capabilities.getMaxAnisotropy();

    return new THREE.MeshLambertMaterial({ map: tex });
  };

  Viz.prototype.cylinder = function (r, h, colour) {
    var THREE = this.THREE;
    return new THREE.Mesh(
      new THREE.CylinderGeometry(r, r, h, 16),
      new THREE.MeshLambertMaterial({ color: colour })
    );
  };

  /** Text as a sprite, always facing the camera. */
  Viz.prototype.textSprite = function (text, scale) {
    var THREE = this.THREE;
    var c = document.createElement('canvas');
    c.width = 256;
    c.height = 96;
    var ctx = c.getContext('2d');

    ctx.font = '600 44px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = 'rgba(35, 37, 42, 0.92)';
    ctx.fillText(String(text), 128, 48);

    var sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({ map: new THREE.CanvasTexture(c), depthWrite: false })
    );
    var s = scale || 1;
    sprite.scale.set(2.2 * s, 0.82 * s, 1);
    return sprite;
  };

  /**
   * Stylised stand-ins per product kind. This is the seam where real GLB
   * models slot in later: swap a case for a loaded model and nothing upstream
   * changes.
   */
  Viz.prototype.buildItem = function (item) {
    var THREE = this.THREE;
    var group = new THREE.Group();
    var trunkBrown = 0x8a6a45;
    var platformGrey = 0x6f7276;
    var skyWhite = 0xf2f2f0;

    if (item.kind === 'tree' || item.kind === 'tower') {
      var height = item.kind === 'tree' ? 1.25 : 1.0;
      var trunk = this.cylinder(0.09, height, trunkBrown);
      trunk.position.y = height / 2;
      group.add(trunk);

      var tiers = item.kind === 'tree' ? [0.6, 1.05, height + 0.06] : [height + 0.04];
      for (var t = 0; t < tiers.length; t++) {
        var disc = this.cylinder(0.3, 0.05, platformGrey);
        disc.position.y = tiers[t];
        group.add(disc);
      }
    } else if (item.kind === 'bridge') {
      for (var s = 0; s < 5; s++) {
        var slat = this.box(0.16, 0.03, 0.34, trunkBrown);
        slat.position.set(-0.4 + s * 0.2, 1.45 - Math.abs(2 - s) * 0.035, 0);
        group.add(slat);
      }
    } else if (item.kind === 'hammock') {
      var sling = this.box(0.55, 0.04, 0.4, 0x3a3a3a);
      sling.position.y = 1.1;
      sling.rotation.z = 0.08;
      group.add(sling);
    } else if (item.kind === 'hideaway') {
      var hut = this.box(0.5, 0.42, 0.42, skyWhite);
      hut.position.y = 1.0;
      group.add(hut);
    } else if (item.kind === 'scratcher') {
      var pad = this.cylinder(0.09, 0.62, platformGrey);
      pad.position.y = 1.0;
      group.add(pad);
    } else {
      var crate = this.box(0.42, 0.42, 0.42, 0x573275);
      crate.position.y = 0.9;
      group.add(crate);
    }

    var tag = this.textSprite(item.title, 0.62);
    tag.position.y = 1.95;
    group.add(tag);

    group.position.set(item.x, 0, item.z);
    if (item.facing) group.rotation.y = item.facing * Math.PI / 180;
    return group;
  };

  /* ------------------------------------------------------------- rebuild */

  Viz.prototype.rebuild = function () {
    if (!this.THREE || !this.journey) return;

    if (!window.CatnetsVizSpec) {
      this.fail('journey-viz-spec.js did not load');
      return;
    }

    var measure = this.journey.data(this.config.measureStepId);
    var spec = window.CatnetsVizSpec.build({
      measure: measure,
      netting: this.journey.data(this.config.nettingStepId),
      hardware: this.journey.data(this.config.hardwareStepId),
      skywalks: this.journey.data(this.config.skywalksStepId)
    });

    if (!spec) {
      this.fail('no measurements to draw (measure step data: ' + JSON.stringify(measure) + ')');
      return;
    }
    this.container.hidden = false;

    var THREE = this.THREE;

    // Replace the previous content wholesale, disposing as we go.
    if (this.contentGroup) {
      this.scene.remove(this.contentGroup);
      this.contentGroup.traverse(function (node) {
        if (node.geometry) node.geometry.dispose();
        if (node.material) {
          if (node.material.map) node.material.map.dispose();
          node.material.dispose();
        }
      });
    }

    var g = new THREE.Group();

    var ground = new THREE.Mesh(
      new THREE.PlaneGeometry(spec.ground.w, spec.ground.d),
      new THREE.MeshLambertMaterial({ color: 0xdfe4dc })
    );
    ground.rotation.x = -Math.PI / 2;
    g.add(ground);

    var i;
    var canvasCache = {};
    for (i = 0; i < spec.walls.length; i++) {
      var wall = spec.walls[i];
      var mesh = new THREE.Mesh(
        new THREE.BoxGeometry(wall.w, wall.h, wall.d),
        this.wallMaterial(wall, canvasCache)
      );
      mesh.position.set(wall.x, wall.y, wall.z);
      g.add(mesh);
    }

    for (i = 0; i < spec.posts.length; i++) {
      var post = this.cylinder(0.05, spec.posts[i].h, 0x565a5e);
      post.position.set(spec.posts[i].x, spec.posts[i].h / 2, spec.posts[i].z);
      g.add(post);
    }

    for (i = 0; i < spec.netPanels.length; i++) {
      g.add(this.netPanel(spec.netPanels[i]));
    }

    for (i = 0; i < spec.markers.length; i++) {
      var m = spec.markers[i];
      var zip = this.box(0.05, 1.6, 0.05, 0x573275);
      zip.position.set(m.x, m.y, m.z);
      if (m.rotY) zip.rotation.y = m.rotY * Math.PI / 180;
      g.add(zip);
    }

    for (i = 0; i < spec.items.length; i++) {
      g.add(this.buildItem(spec.items[i]));
    }

    for (i = 0; i < spec.labels.length; i++) {
      var label = this.textSprite(spec.labels[i].text, 1);
      label.position.set(spec.labels[i].x, spec.labels[i].y + 0.45, spec.labels[i].z);
      g.add(label);
    }

    this.scene.add(g);
    this.contentGroup = g;

    // Frame the scene from its bounds.
    var span = Math.max(spec.bounds.x, spec.bounds.z, spec.bounds.y * 2);
    this.distance = span * 1.15 + 3;
    this.target.set(0, spec.bounds.y * 0.45, 0);

    if (this.captionEl) {
      var netPanel = spec.netPanels[0];
      this.captionEl.textContent = netPanel && netPanel.label ? netPanel.label : '';
    }

    this.resize();
  };

  function boot() {
    var roots = document.querySelectorAll('[data-journey-step-source]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].querySelector('[data-viz-config]') && !roots[i].hasAttribute('data-viz-bound')) {
        roots[i].setAttribute('data-viz-bound', '');
        new Viz(roots[i]);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // The theme editor re-renders a section whenever its settings change, which
  // replaces the DOM and orphans the running instance. Fresh markup has no
  // data-viz-bound attribute, so boot() picks it up and starts a new one.
  document.addEventListener('shopify:section:load', boot);
})();
